import { renderPageShell } from '../lib/page-shell';
import { workerHttpUrl, workerStackTowerMatchSocketUrl } from '../lib/worker-config';
import { fetchMe, getToken, mintConnectionTicket } from '../lib/session';
import { attachStackTowerMatchSession } from '../lib/stack-tower-match-session';
import { fetchStackTowerMulti60Leaderboard } from '../lib/stack-tower';
import { escapeHtml } from '../lib/format';
import type { RouteHandler } from '../lib/router';

/**
 * C015 Phase 3-5 entry point — create/join a Stack Tower head-to-head by room
 * code, mirroring `online-duel.ts`'s menu shape. A guest is asked for a name
 * once before connecting; a signed-in player's real username is resolved
 * automatically and (Phase 5) a connection ticket is minted the same way
 * `online-duel.ts` does, so the server can record their match history and
 * offer them a real identity-checked reconnect.
 */
export const renderStackTowerOnline: RouteHandler = (container, navigate, query) => {
  const body = renderPageShell(container, navigate, 'play', {
    title: 'STACK TOWER: HEAD TO HEAD',
    subtitle: 'Two towers, side by side. Pick 60 Second (a shared clock, a miss costs your tower not your score) or Endless (no clock, a miss ends your run for good).',
    body: `<div id="stack-tower-online-mount"></div>`,
  });

  const mount = body.querySelector<HTMLDivElement>('#stack-tower-online-mount')!;
  let socket: WebSocket | null = null;
  let destroySession: (() => void) | void;
  let guestName: string | null = null;
  // Only meaningful when CREATING — a room commits to whichever mode its
  // first connection names (stack-tower-match.ts), so joining an existing
  // room by code never needs this; the room already knows what it is.
  let createMode: 'timed' | 'endless' = 'timed';

  function cleanup() {
    if (destroySession) destroySession();
    destroySession = undefined;
    if (socket) {
      socket.onclose = null;
      socket.close();
      socket = null;
    }
  }

  function renderMenu() {
    cleanup();
    mount.innerHTML = `
      <div class="choice-grid">
        <div class="choice-card">
          <strong>Create a match</strong>
          <small>Get a room code, share it with a friend.</small>
          <div class="choice-toggle" role="radiogroup" aria-label="Match mode">
            <button class="choice-toggle__option is-selected" type="button" data-mode="timed" aria-pressed="true">60 Second</button>
            <button class="choice-toggle__option" type="button" data-mode="endless" aria-pressed="false">Endless</button>
          </div>
          <p class="settings-hint" id="stack-tower-mode-hint">A shared 60s clock. A miss resets your tower, not your score.</p>
          <button class="button button--primary" type="button" data-action="create">Create match</button>
        </div>
        <div class="choice-card">
          <strong>Join a match</strong>
          <small>Enter the code your friend shared.</small>
          <input class="join-input" id="join-code" maxlength="8" placeholder="ROOM CODE" aria-label="Room code" autocomplete="off" />
          <button class="button button--secondary" type="button" data-action="join">Join match</button>
        </div>
      </div>
      <p class="page-body__stub"><a href="#" data-action="leaderboard">View 60 Second leaderboard</a></p>
    `;
    const modeHint = mount.querySelector<HTMLParagraphElement>('#stack-tower-mode-hint')!;
    mount.querySelectorAll<HTMLButtonElement>('.choice-toggle__option').forEach((button) => {
      button.addEventListener('click', () => {
        createMode = button.dataset.mode === 'endless' ? 'endless' : 'timed';
        mount.querySelectorAll<HTMLButtonElement>('.choice-toggle__option').forEach((b) => {
          const selected = b === button;
          b.classList.toggle('is-selected', selected);
          b.setAttribute('aria-pressed', String(selected));
        });
        modeHint.textContent =
          createMode === 'endless'
            ? 'No clock. A miss ends your run for good. Surpass their score, or outlast them.'
            : 'A shared 60s clock. A miss resets your tower, not your score.';
      });
    });
    mount.querySelector<HTMLButtonElement>('[data-action="create"]')?.addEventListener('click', () => void handleCreate());
    mount.querySelector<HTMLButtonElement>('[data-action="join"]')?.addEventListener('click', () => {
      const input = mount.querySelector<HTMLInputElement>('#join-code');
      const code = input?.value.trim().toUpperCase();
      if (code) void withName(() => connectToRoom(code));
    });
    mount.querySelector<HTMLAnchorElement>('[data-action="leaderboard"]')?.addEventListener('click', (event) => {
      event.preventDefault();
      void renderMulti60Leaderboard();
    });
  }

  /** A raw high-score board for 60-second matches (C017 Phase 3, spec §115) — separate from the ranked Elo ladder at /#stack-tower-ranked. */
  async function renderMulti60Leaderboard(): Promise<void> {
    renderConnecting('Loading leaderboard…');
    try {
      const board = await fetchStackTowerMulti60Leaderboard();
      mount.innerHTML = `
        <div class="handoff">
          <p class="handoff__label">60 Second top scores</p>
          <ol class="attempt-list">
            ${
              board.leaderboard.length === 0
                ? `<li class="attempt-list__empty">Nobody has posted a score yet. Be the first.</li>`
                : board.leaderboard
                    .map((row) => `<li><span>#${row.rank}</span><span>${escapeHtml(row.username)}</span><strong>${row.score}</strong></li>`)
                    .join('')
            }
          </ol>
          <div class="hero-actions">
            <button class="button button--secondary" type="button" data-action="menu">Back</button>
          </div>
        </div>
      `;
      mount.querySelector<HTMLButtonElement>('[data-action="menu"]')?.addEventListener('click', renderMenu);
    } catch (error) {
      mount.innerHTML = `
        <div class="handoff">
          <p class="handoff__label">Error</p>
          <h2>${escapeHtml(error instanceof Error ? error.message : 'Could not load the leaderboard.')}</h2>
          <button class="button button--secondary" type="button" data-action="menu">Back</button>
        </div>
      `;
      mount.querySelector<HTMLButtonElement>('[data-action="menu"]')?.addEventListener('click', renderMenu);
    }
  }

  function renderConnecting(message: string) {
    mount.innerHTML = `<div class="handoff"><p class="handoff__label">${message}</p></div>`;
  }

  function renderNamePrompt(onSubmit: (name: string) => void) {
    mount.innerHTML = `
      <form class="choice-card" data-role="name-form">
        <strong>Enter a name for this match</strong>
        <input class="join-input" data-role="name-input" maxlength="24" placeholder="Your name" aria-label="Display name" autocomplete="off" required />
        <button class="button button--primary" type="submit">Continue</button>
      </form>
    `;
    const form = mount.querySelector<HTMLFormElement>('[data-role="name-form"]')!;
    form.addEventListener('submit', (event) => {
      event.preventDefault();
      const input = form.querySelector<HTMLInputElement>('[data-role="name-input"]')!;
      const name = input.value.trim();
      if (!name) return;
      guestName = name;
      onSubmit(name);
    });
  }

  /** Signed-in players use their real username automatically; a guest is asked once per visit to this view. */
  async function withName(next: () => void): Promise<void> {
    if (guestName) {
      next();
      return;
    }
    const me = await fetchMe().catch(() => null);
    if (me) {
      guestName = me.user.username;
      next();
      return;
    }
    renderNamePrompt(() => next());
  }

  /**
   * Only ever called once `guestName` is resolved — every call site routes
   * through `withName` first. `mode` only matters when this connection is
   * the one that CREATES the room (the DO commits to whichever mode its
   * first-ever connection names) — omitted when joining an existing room by
   * code, since the room already knows what it is.
   */
  async function connectToRoom(code: string, mode: 'timed' | 'endless' | null = null) {
    renderConnecting(`Connecting to ${code}…`);
    cleanup();
    // Signed-in players connect with a single-use ticket (never the session
    // token). A guest — or a mint failure — falls back to the already-resolved
    // name, which for a signed-in player is still their real username.
    let connectionTicket: string | null = null;
    if (getToken()) {
      try {
        connectionTicket = await mintConnectionTicket();
      } catch {
        connectionTicket = null;
      }
    }
    socket = new WebSocket(workerStackTowerMatchSocketUrl(code, guestName!, connectionTicket, null, mode));
    socket.onclose = () => {
      socket = null;
    };
    socket.onerror = () => {
      mount.innerHTML = `
        <div class="handoff">
          <p class="handoff__label">Error</p>
          <h2>Could not reach the match server.</h2>
          <button class="button button--secondary" type="button" data-action="menu">Back to menu</button>
        </div>
      `;
      mount.querySelector<HTMLButtonElement>('[data-action="menu"]')?.addEventListener('click', renderMenu);
    };
    destroySession = attachStackTowerMatchSession(mount, socket, {
      lobbyHeading: `Room ${code}`,
      onExit: () => {
        cleanup();
        renderMenu();
      },
    });
  }

  async function handleCreate() {
    await withName(async () => {
      renderConnecting('Creating a room…');
      try {
        const response = await fetch(`${workerHttpUrl()}/api/stack-tower/match`, { method: 'POST' });
        if (!response.ok) throw new Error(`server responded ${response.status}`);
        const data = (await response.json()) as { code: string };
        void connectToRoom(data.code, createMode);
      } catch {
        mount.innerHTML = `
          <div class="handoff">
            <p class="handoff__label">Error</p>
            <h2>Could not reach the match server. Is it running?</h2>
            <button class="button button--secondary" type="button" data-action="menu">Back to menu</button>
          </div>
        `;
        mount.querySelector<HTMLButtonElement>('[data-action="menu"]')?.addEventListener('click', renderMenu);
      }
    });
  }

  // Arriving from an accepted direct challenge (`stack-tower-online?code=XYZ`)
  // drops the player straight into that room; otherwise show the create/join menu.
  const presetCode = query?.get('code')?.trim().toUpperCase();
  if (presetCode) {
    void withName(() => connectToRoom(presetCode));
  } else {
    renderMenu();
  }

  return () => cleanup();
};
